import { useMemo } from 'react';
import { FieldValues, useController, UseControllerProps, useFormContext } from 'react-hook-form';
import { HookFormCustomReturn } from '../useForm/types';

interface CustomControllerProps<
  TFieldValues extends FieldValues = FieldValues,
> extends UseControllerProps<TFieldValues> {
  disabled?: boolean;
}

export function useFormController<TFieldValues extends FieldValues = FieldValues>(
  props: CustomControllerProps<TFieldValues>
) {
  const form = useFormContext<TFieldValues>() as HookFormCustomReturn<TFieldValues>;
  const { field, fieldState, formState } = useController({ ...props, control: form.control });

  const { disabled, name } = props;

  const isFieldDisabled = useMemo(() => {
    // Check if getFieldDisabled exists (custom form) or use props.disabled
    if (form.getFieldDisabled && typeof form.getFieldDisabled === 'function') {
      return Boolean(form.getFieldDisabled(name) ?? disabled ?? false);
    }
    return Boolean(disabled ?? false);
  }, [form, name, disabled]);

  return {
    field: {
      ...field,
      disabled: isFieldDisabled,
    },
    fieldState,
    formState,
  };
}
